import { createBrowserRouter } from "react-router-dom";
import { Suspense } from "react";
import Layout from "../layouts/MainLayout";
import { ProtectedRoute } from "./ProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import RouteError from "../shared/components/ErrorBoundary/RouteError";
import {
  AccountsList,
  AccountView,
  Login,
  Dashboard,
  StaffProfile,
  CustomersList,
  CustomerView,
  DriversList,
  DriverView,
  BookingsList,
  BookingView,
  BookingActionsView,
  EditBooking,
  CreateBooking,
  CreateSchedule,
  EditSchedule,
  MassEditBookings,
  ScheduleView,
  ScheduleActionsView,
  SchedulesList,
  Regions,
  AuthorityView,
  ConfigManagement,
  LiveMap,
  AuditLogs,
  Notifications,
  OneSignal,
  LogMonitor,
  Finance,
  AuthManagement,
  StaffManagement,
  LiveLogs,
  GhostDocuments,
  DataMigration,
  AppVersionSettings,
} from "./lazyComponents";
import { Loader } from "@mantine/core";
import CalendarList from "../pages/Calendars/CalendarList";
import CreateCalendar from "../pages/Calendars/CreateCalendar";
import CalendarView from "../pages/Calendars/CalendarView";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/live-logs",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <LiveLogs />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/auth-management",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AuthManagement />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/staff-management",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <StaffManagement />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/accounts",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AccountsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/accounts/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AccountView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/customers",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CustomersList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/customers/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CustomerView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/drivers",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <DriversList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/drivers/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <DriverView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <BookingsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <BookingView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/:id/actions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <BookingActionsView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/:id/edit",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <EditBooking />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/create",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CreateBooking />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/calendars",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loader color="blue" size="xl" type="bars" />}>
              <CalendarList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/calendars/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loader color="blue" size="xl" type="bars" />}>
              <CalendarView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/calendars/create",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loader color="blue" size="xl" type="bars" />}>
              <CreateCalendar />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/create",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CreateSchedule />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/:id/edit",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <EditSchedule />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/mass-edit/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <MassEditBookings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <ScheduleView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/:id/actions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <ScheduleActionsView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <SchedulesList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/regions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Regions />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/local-authority/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AuthorityView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/config-management",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <ConfigManagement />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/app-versions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AppVersionSettings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/live-map",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <LiveMap />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/audit-logs",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AuditLogs />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/notifications",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Notifications />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/onesignal",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <OneSignal />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/log-monitor",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <LogMonitor />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/finance",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Finance />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/staff/profile",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <StaffProfile />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/dev-tools/data-migration",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <DataMigration />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/dev-tools/ghost-documents",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <GhostDocuments />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <RouteError />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/auth",
    element: (
      <PublicRoute>
        <Suspense fallback={<div>Loading...</div>}>
          <Login />
        </Suspense>
      </PublicRoute>
    ),
  },
]);
